import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "../components/sudanLanding.scss";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SudanMap from "../assets/Sudan-Map-plain.svg";
import HeroVisual from "../assets/banner-humanitarian-reset.png";
import PillarsImg from "../assets/humanitarian-need.png";
import { LanguageProvider, useLanguage } from "../i18n/LanguageContext";

const RESET_STATS = [
   { num: "70%", label: "IASC aspiration for pooled funding to go directly to local organizations" },
   { num: "52%", label: "Current share of pooled funding allocated to local actors since February 2025" },
   { num: "March 2025", label: "Launch of the Humanitarian Reset by UN ERC Tom Fletcher" }
];

const PILLARS = [
   {
      title: "Define — greatest need",
      text: "KAPorg assessments are rooted in community need and organizational context, not externally imposed donor templates."
   },
   {
      title: "Devolve — local leadership",
      text: "The platform empowers Sudanese organizations to lead their own capacity development — building the evidence base for direct funding."
   },
   {
      title: "Deliver — accountability",
      text: "KAPorg tools strengthen financial management, donor compliance, and program monitoring — making Sudanese organizations accountable partners."
   },
   {
      title: "Defend — principles",
      text: "All KAPorg assessments and certifications are aligned with humanitarian principles and internationally recognized compliance standards."
   }
];

const FRONTLINE_STATS = [
   { num: "16.9M", label: "Displaced people in need of humanitarian assistance in Sudan" },
   { num: "11,900+", label: "Organizations listed in the federal and state register with the Humanitarian Aid Commission" },
   { num: "18", label: "States across which Sudanese organizations are delivering humanitarian response" },
   { num: "12+", label: "Humanitarian crossings allowing aid to flow through land, sea, and air" }
];

const HumanitarianResetInner = () => {
   const [openPillar, setOpenPillar] = useState(0);
   const { t, dir, lang } = useLanguage();

   useEffect(() => {
      window.scrollTo(0, 0);
   }, []);

   return (
      <div className="App">
         <div
            className={`sudan-kaporg sudan-landing ${lang === "ar" ? "sudan-rtl" : ""}`}
            dir={dir}
         >
            <Navbar />

            {/* Hero */}
            <section className="sd-page-hero">
               <div className="container">
                  <div className="sd-page-hero-inner">
                     <div className="sd-page-hero-text">
                        <span className="sd-chip">{t("Humanitarian Reset")}</span>
                        <h1>{t("The System is Changing. Claim Your Place")}</h1>
                        <p>
                           {t(
                              "The rules of humanitarian funding are changing. For the first time, local organizations have a real path to direct funding — but only if they can demonstrate they are ready."
                           )}
                        </p>
                        <div className="sd-page-hero-cta">
                           <Link to="/auth/registration" className="sd-btn sd-btn-accent">
                              {t("Register for free")}
                           </Link>
                           <Link
                              to="/capacity-accelerator-program"
                              className="sd-btn sd-btn-ghost-light"
                           >
                              {t("Capacity Accelerator Program")}
                           </Link>
                        </div>
                     </div>
                     <div className="sd-page-hero-visual">
                        <img src={HeroVisual} alt="" aria-hidden="true" />
                     </div>
                  </div>
               </div>
            </section>

            {/* Section 1 - global commitment + stat cards */}
            <section className="sd-section sd-bg-white">
               <div className="container">
                  <div className="sd-split stretch">
                     <div className="sd-split-main">
                        <h2 className="sd-block-title">
                           {t("A global commitment to shift power to local actors.")}
                        </h2>
                        <p className="sd-block-text">
                           {t(
                              "In March 2025, the UN Emergency Relief Coordinator launched the Humanitarian Reset — a global commitment to make the humanitarian system faster, lighter, more accountable, and more impactful."
                           )}
                        </p>
                        <p className="sd-block-text">
                           {t(
                              "At its core is a fundamental shift of power, funding, and decision-making to local and national actors. The IASC Roadmap sets an aspiration of 70% of pooled funding channelled directly to local organizations — up from 52% today."
                           )}
                        </p>
                        <p className="sd-block-text">
                           {t(
                              "This is not a promise. It is a target with a roadmap, endorsed by the Inter-Agency Standing Committee and backed by the UN's Emergency Relief Coordinator. The direction is set. The question for local organizations is whether they are positioned to benefit from it."
                           )}
                        </p>
                     </div>
                     <div className="sd-split-aside">
                        <div
                           className="sd-card-grid sd-stat-stack"
                           style={{ gridTemplateColumns: "1fr" }}
                        >
                           {RESET_STATS.map((stat) => (
                              <div className="sd-card" key={stat.num}>
                                 <div
                                    className="sd-card-index"
                                    style={{ fontSize: "32px", color: "#003daa" }}
                                 >
                                    {t(stat.num)}
                                 </div>
                                 <p>{t(stat.label)}</p>
                              </div>
                           ))}
                        </div>
                     </div>
                  </div>
               </div>
            </section>

            {/* Section 2 - four pillars (image + expandable accordion, mirrors the
                "We adapt to the need of your industry" block on kaporg.com) */}
            <section className="sd-section sd-bg-light">
               <div className="container">
                  <div className="sd-pillars-split">
                     <div className="sd-pillars-media">
                        <h2>{t("How KAPorg supports the Reset's four pillars.")}</h2>
                        <p>
                           {t(
                              "The Reset is organized around four core commitments. KAPorg is designed to support each of them at the organizational level."
                           )}
                        </p>
                        <div className="sd-pillars-photo">
                           <img
                              src={PillarsImg}
                              alt="Community members gathered around a humanitarian aid vehicle in Sudan"
                           />
                        </div>
                     </div>
                     <div className="sd-accordion sd-accordion-flush">
                        {PILLARS.map((pillar, index) => (
                           <div
                              className={`sd-accordion-item ${openPillar === index ? "open" : ""}`}
                              key={pillar.title}
                           >
                              <button
                                 type="button"
                                 className="sd-accordion-head"
                                 aria-expanded={openPillar === index}
                                 onClick={() =>
                                    setOpenPillar(openPillar === index ? -1 : index)
                                 }
                              >
                                 <h4>{t(pillar.title)}</h4>
                                 <span className="sd-accordion-icon">
                                    {openPillar === index ? "−" : "+"}
                                 </span>
                              </button>
                              <div className="sd-accordion-body">
                                 <p>{t(pillar.text)}</p>
                              </div>
                           </div>
                        ))}
                     </div>
                  </div>
               </div>
            </section>

            {/* Section 3 - Sudan frontlines + map */}
            <section className="sd-section sd-bg-white sd-reset-frontline">
               <div className="container">
                  <div className="sd-section-head" style={{ textAlign: "left", margin: "0 0 28px" }}>
                     <h2 style={{ fontSize: "32px" }}>
                        {t(
                           "Sudan's organizations are on the frontlines. KAPorg gives them the tools to prove it."
                        )}
                     </h2>
                     <p>
                        {t(
                           "Sudan has 11,900 registered organizations delivering humanitarian action across 18 states. These organizations have the knowledge, the relationships, and the presence. What KAPorg gives them is the evidence base to prove it to the world."
                        )}
                     </p>
                  </div>
                  <div className="sd-split sd-frontline-split" style={{ alignItems: "flex-start" }}>
                     <div className="sd-split-main">
                        <div className="sd-card-grid cols-2">
                           {FRONTLINE_STATS.map((stat) => (
                              <div className="sd-card" key={stat.num}>
                                 <div
                                    className="sd-card-index"
                                    style={{ fontSize: "30px", color: "#003daa" }}
                                 >
                                    {t(stat.num)}
                                 </div>
                                 <p>{t(stat.label)}</p>
                              </div>
                           ))}
                        </div>
                     </div>
                     <div className="sd-split-aside sd-frontline-map">
                        <img src={SudanMap} alt="Map of Sudan" />
                     </div>
                  </div>
               </div>
            </section>

            {/* Section 4 - closing */}
            <section className="sd-section sd-closing sd-reset-closing">
               <div className="container">
                  <div className="sd-closing-inner">
                     <h2>
                        {t("The Reset is an opportunity.")}
                        <br />
                        {t("Make sure your organization is ready for it.")}
                     </h2>
                     <p>
                        {t(
                           "Register on KAPorg today and start building the evidence base that international donors require."
                        )}
                     </p>
                     <div className="sd-closing-cta">
                        <Link to="/auth/registration" className="sd-btn sd-btn-accent">
                           {t("Register for free")}
                        </Link>
                        <Link
                           to="/capacity-accelerator-program"
                           className="sd-btn sd-btn-ghost-light"
                        >
                           {t("Capacity Accelerator Program")}
                        </Link>
                     </div>
                  </div>
               </div>
            </section>

            <Footer />
         </div>
      </div>
   );
};

const HumanitarianResetPage = () => (
   <LanguageProvider>
      <HumanitarianResetInner />
   </LanguageProvider>
);

export default HumanitarianResetPage;
